/* Generated by restful-react */

import React from 'react'
import {
  Get,
  GetProps,
  useGet,
  UseGetProps,
  Mutate,
  MutateProps,
  useMutate,
  UseMutateProps,
} from 'restful-react'

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

export interface ErrorResponse {
  errors: string[]
}

export interface User {
  /**
   * User ID
   */
  id?: number
  /**
   * First Name
   */
  firstName?: string | null
  /**
   * Last Name
   */
  lastName?: string | null
  /**
   * Phone Number
   */
  phone?: string | null
  /**
   * Email
   */
  email?: string
}

/**
 * Shipment Object
 */
export interface Shipment {
  /**
   * Shipment ID
   */
  id?: number
  bouncedCount?: number | null
  driver?: User
  dispatcher?: User
  hasBackhaulEligibleLane?: boolean | null
  hasHazmat?: boolean | null
  hot?: boolean | null
  pricedUnderContract?: boolean | null
  team?: boolean | null
  /**
   * Trailer Number
   */
  trailerNumber?: string | null
  /**
   * Truck Number
   */
  truckNumber?: string | null
  /**
   * Vehicle Identification Number
   */
  vin?: string | null
  rolloverCount?: number
  shipperApplication?: ShipperApplication
  status?: ShipmentStatus
  equipment?: Equipment
  shipperTrackingSegment?: ShipperTrackingSegment
  trackingStatus?: TrackingStatus
  /**
   * Shipment's pickups
   */
  pickups?: ShipmentStop[]
  /**
   * Shipment's deliveries
   */
  deliveries?: ShipmentStop[]
  /**
   * Shipment's Lane Information
   */
  lane?: string
  missingFields?: string[]
  /**
   * Shipment issues information
   */
  openShipmentIssuesCount?: number
}

/**
 * Shipment for Update
 */
export interface ShipmentUpdate {
  driverId?: number
  /**
   * Trailer Number
   */
  trailerNumber?: string | null
  /**
   * Truck Number
   */
  truckNumber?: string | null
  /**
   * Vehicle Identification Number
   */
  vin?: string | null
  /**
   * Shipment's pickups
   */
  pickupsAttributes?: ShipmentStopUpdate[]
  /**
   * Shipment's deliveries
   */
  deliveriesAttributes?: ShipmentStopUpdate[]
}

/**
 * Shipment Equipment Information
 */
export interface Equipment {
  icon?: string
  name?: string
  parentId?: number
  attributes?: {
    straps?: string
  }
}

/**
 * Shipper Application Information
 */
export interface ShipperApplication {
  id?: number
  name?: string
}

/**
 * Shipment's Shipper Tracking Segment
 */
export interface ShipperTrackingSegment {
  id?: number
  minReqTrackingPercentage?: number | null
  description?: string | null
  level?: number | null
}

/**
 * Shipment Tracking Status
 */
export interface TrackingStatus {
  trackingState?: string
  trackingMethod?: string
}

/**
 * Shipment Status Information
 */
export interface ShipmentStatus {
  name?: string
  state?: string
  key?: string
  pickupStatus?: string
  currentStopIndex?: number
  calculatedMiles?: number
  eta?: string | null
  timeLateToNextStop?: string | null
  manualEta?: string | null
  project44Eta?: string | null
}

/**
 * Shipment Stop Object
 */
export interface ShipmentStop {
  /**
   * Shipment Stop ID
   */
  id?: number
  /**
   * Stop Type
   */
  stopType?: string
  /**
   * Stop Sequence Number
   */
  stopSequence?: number
  /**
   * Is Drop Trailer?
   */
  isDropTrailer?: boolean | null
  /**
   * Manual ETA for stop
   */
  manualEta?: string | null
  /**
   * Reason that stop is expected to be late
   */
  lateReason?: string | null
  /**
   * Check-In DateTime
   */
  arrivedAt?: string | null
  /**
   * Check-Out DateTime
   */
  leftAt?: string | null
  /**
   * Trailer Emptied DateTime
   */
  trailerEmptiedAt?: string | null
  /**
   * City
   */
  city?: string
  /**
   * State Code
   */
  stateCode?: string
  /**
   * Appointment Type confirmed or unconfirmed
   */
  appointmentType?: string
  /**
   * Appointment reschedule requested?
   */
  hasRescheduleRequest?: boolean
  appointment?: ShipmentStopAppointment
  missingFields?: string[]
}

/**
 * Shipment Stop Appointment
 */
export interface ShipmentStopAppointment {
  appointmentType?: string
  startAt?: string
  endAt?: string
  timezone?: string
  status?: string
}

/**
 * Shipment Stop attributes for Update
 */
export interface ShipmentStopUpdate {
  /**
   * Shipment Stop ID
   */
  id?: number
  /**
   * Manual ETA for stop
   */
  manualEta?: string | null
  /**
   * Reason that stop is expected to be late
   */
  lateReason?: string | null
  /**
   * Check-In DateTime
   */
  arrivedAt?: string | null
  /**
   * Check-Out DateTime
   */
  leftAt?: string | null
  /**
   * Trailer Emptied DateTime
   */
  trailerEmptiedAt?: string | null
}

export interface GetCarrierActiveShipmentsResponse {
  dispatchers?: User[]
  shipments?: Shipment[]
}

export interface GetCarrierActiveShipmentsPathParams {
  carrierId: number
}

export interface GetCarrierActiveShipmentsRequestBody {
  [key: string]: any
}

export type GetCarrierActiveShipmentsProps = Omit<
  GetProps<
    GetCarrierActiveShipmentsResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsPathParams
  >,
  'path'
> &
  GetCarrierActiveShipmentsPathParams

/**
 * Shipments List
 */
export const GetCarrierActiveShipments = ({
  carrierId,
  ...props
}: GetCarrierActiveShipmentsProps) => (
  <Get<GetCarrierActiveShipmentsResponse, ErrorResponse, void, GetCarrierActiveShipmentsPathParams>
    path={`/v9/carriers/${carrierId}/active_shipments`}
    {...props}
  />
)

export type UseGetCarrierActiveShipmentsProps = Omit<
  UseGetProps<
    GetCarrierActiveShipmentsResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsPathParams
  >,
  'path'
> &
  GetCarrierActiveShipmentsPathParams

/**
 * Shipments List
 */
export const useGetCarrierActiveShipments = ({
  carrierId,
  ...props
}: UseGetCarrierActiveShipmentsProps) =>
  useGet<
    GetCarrierActiveShipmentsResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsPathParams
  >(
    (paramsInPath: GetCarrierActiveShipmentsPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/active_shipments`,
    { pathParams: { carrierId }, ...props }
  )

export interface GetCarrierActiveShipmentsCountResponse {
  count?: number
}

export interface GetCarrierActiveShipmentsCountPathParams {
  carrierId: number
}

export type GetCarrierActiveShipmentsCountProps = Omit<
  GetProps<
    GetCarrierActiveShipmentsCountResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsCountPathParams
  >,
  'path'
> &
  GetCarrierActiveShipmentsCountPathParams

/**
 * Active Shipments List Count
 */
export const GetCarrierActiveShipmentsCount = ({
  carrierId,
  ...props
}: GetCarrierActiveShipmentsCountProps) => (
  <Get<
    GetCarrierActiveShipmentsCountResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsCountPathParams
  >
    path={`/v9/carriers/${carrierId}/active_shipments/count`}
    {...props}
  />
)

export type UseGetCarrierActiveShipmentsCountProps = Omit<
  UseGetProps<
    GetCarrierActiveShipmentsCountResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsCountPathParams
  >,
  'path'
> &
  GetCarrierActiveShipmentsCountPathParams

/**
 * Active Shipments List Count
 */
export const useGetCarrierActiveShipmentsCount = ({
  carrierId,
  ...props
}: UseGetCarrierActiveShipmentsCountProps) =>
  useGet<
    GetCarrierActiveShipmentsCountResponse,
    ErrorResponse,
    void,
    GetCarrierActiveShipmentsCountPathParams
  >(
    (paramsInPath: GetCarrierActiveShipmentsCountPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/active_shipments/count`,
    { pathParams: { carrierId }, ...props }
  )

export interface UpdateCarrierActiveShipmentPathParams {
  carrierId: number
  shipmentId: number
}

export type UpdateCarrierActiveShipmentProps = Omit<
  MutateProps<Shipment, ErrorResponse, void, ShipmentUpdate, UpdateCarrierActiveShipmentPathParams>,
  'path' | 'verb'
> &
  UpdateCarrierActiveShipmentPathParams

/**
 * Update Shipment
 */
export const UpdateCarrierActiveShipment = ({
  carrierId,
  shipmentId,
  ...props
}: UpdateCarrierActiveShipmentProps) => (
  <Mutate<Shipment, ErrorResponse, void, ShipmentUpdate, UpdateCarrierActiveShipmentPathParams>
    verb='PUT'
    path={`/v9/carriers/${carrierId}/active_shipments/${shipmentId}`}
    {...props}
  />
)

export type UseUpdateCarrierActiveShipmentProps = Omit<
  UseMutateProps<
    Shipment,
    ErrorResponse,
    void,
    ShipmentUpdate,
    UpdateCarrierActiveShipmentPathParams
  >,
  'path' | 'verb'
> &
  UpdateCarrierActiveShipmentPathParams

/**
 * Update Shipment
 */
export const useUpdateCarrierActiveShipment = ({
  carrierId,
  shipmentId,
  ...props
}: UseUpdateCarrierActiveShipmentProps) =>
  useMutate<Shipment, ErrorResponse, void, ShipmentUpdate, UpdateCarrierActiveShipmentPathParams>(
    'PUT',
    (paramsInPath: UpdateCarrierActiveShipmentPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/active_shipments/${paramsInPath.shipmentId}`,
    { pathParams: { carrierId, shipmentId }, ...props }
  )
